/**
 * Parent-side JSX compile + srcdoc builder for interactive artefacts (ADR-0097).
 * Sucrase transform is string→string only — the parent never evals model output.
 */

import { transform } from "sucrase";

/** CSP embedded in the artifact srcdoc (opaque-origin iframe). */
export const ARTIFACT_CSP =
  "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:; connect-src 'none'; frame-ancestors 'none'";

/** Exact sandbox token list for the consumer iframe. */
export const ARTIFACT_IFRAME_SANDBOX = "allow-scripts";

type CompileArtifactJsxResult = { ok: true; compiledJs: string } | { ok: false; error: string };

/**
 * Strip module export syntax so compiled output runs as a classic script.
 */
function normalizeArtifactSource(source: string): string {
  return source
    .replace(/^\s*export\s+default\s+/m, "")
    .replace(/^\s*export\s+(?=function|const|class|let|var)/gm, "");
}

export function compileArtifactJsx(source: string): CompileArtifactJsxResult {
  try {
    const normalized = normalizeArtifactSource(source);
    const result = transform(normalized, {
      transforms: ["jsx", "typescript"],
      jsxRuntime: "classic",
      production: true,
    });
    return { ok: true, compiledJs: result.code };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, error: message };
  }
}

export function buildArtifactSrcdoc(opts: { compiledJs: string; runtimeJs: string }): string {
  const { compiledJs, runtimeJs } = opts;
  // Escape </script> sequences so model output cannot break out of the script tag.
  const safeRuntime = runtimeJs.replace(/<\/script/gi, "<\\/script");
  const safeCompiled = compiledJs.replace(/<\/script/gi, "<\\/script");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8"/>
<meta http-equiv="Content-Security-Policy" content="${ARTIFACT_CSP}"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>Artifact</title>
<style>
  html, body { margin: 0; padding: 0; min-height: 100%; font-family: system-ui, sans-serif; }
  #root { min-height: 100vh; box-sizing: border-box; }
  #artifact-error { color: #b91c1c; padding: 1rem; font-size: 14px; white-space: pre-wrap; }
</style>
</head>
<body>
<div id="root"></div>
<script>${safeRuntime}</script>
<script>
(function () {
  var rootEl = document.getElementById("root");
  function showError(msg) {
    var el = document.createElement("pre");
    el.id = "artifact-error";
    el.textContent = msg;
    document.body.textContent = "";
    document.body.appendChild(el);
  }
  try {
    var rt = typeof ArtifactRuntime !== "undefined" ? ArtifactRuntime : null;
    if (!rt || !rt.React || !rt.createRoot) {
      showError("Artifact runtime failed to load.");
      return;
    }
    var React = rt.React;
    var createRoot = rt.createRoot;
    ${safeCompiled}
    var Comp =
      (typeof Artifact !== "undefined" && Artifact) ||
      (typeof App !== "undefined" && App) ||
      null;
    if (typeof Comp !== "function") {
      showError("Interactive artefact must define function Artifact or App.");
      return;
    }
    createRoot(rootEl).render(React.createElement(Comp));
  } catch (err) {
    showError(err && err.message ? String(err.message) : String(err));
  }
})();
</script>
</body>
</html>`;
}
